import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiGrid,
  FiBarChart2,
  FiUsers,
  FiFeather,
  FiAlertTriangle,
  FiVideo,
  FiCalendar,
  FiSettings,
  FiBell,
  FiLogOut,
  FiMessageSquare,
  FiX,
  FiChevronRight,
  FiMenu
} from "react-icons/fi";
import { MdOutlineSoupKitchen } from "react-icons/md";
import { logout } from "../../utils/auth";
import adminApi from "../../services/adminApi";
import useAdminProfile from "../../hooks/useAdminProfile";

export default function AdminMobileDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const adminProfile = useAdminProfile();
  const [counts, setCounts] = useState({
    users: 0,
    subs: 0,
    priority: 0,
    sessions: 0,
    queries: 0
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await adminApi.getDashboardStats();
        if (res.success && res.data) {
          const raw = res.data;
          setCounts({
            users: raw.totalUsers ?? raw.TotalUsers ?? 0,
            subs: raw.activeSubscriptions ?? raw.ActiveSubscriptions ?? 0,
            priority: (raw.priorityAlerts || raw.PriorityAlerts || {}).total ?? (raw.priorityAlerts || raw.PriorityAlerts || {}).Total ?? 0,
            sessions: raw.totalLiveSessions ?? raw.TotalLiveSessions ?? 0,
            queries: raw.pendingQueriesCount ?? raw.PendingQueriesCount ?? 0
          });
        }
      } catch (err) {
        console.warn("Failed to load admin stats for mobile nav:", err);
      }
    };
    fetchCounts();
  }, []);

  // Close sheet on route change and scroll active tab into view
  useEffect(() => {
    setSheetOpen(false);
    if (scrollContainerRef.current) {
      const activeEl = scrollContainerRef.current.querySelector("[data-active='true']");
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Full Horizontal Scrollable Navigation Items
  const dockNavItems = [
    { id: "dashboard", label: "Overview", path: "/admin/dashboard", icon: FiGrid },
    { id: "users", label: "Users", path: "/admin/usermanagement", icon: FiUsers, badge: counts.users ? (counts.users > 99 ? "99+" : counts.users) : null },
    { id: "livesessions", label: "Live", path: "/admin/livesession", icon: FiCalendar },
    { id: "queries", label: "Queries", path: "/admin/queries", icon: FiMessageSquare, badge: counts.queries || null },
    { id: "subscriptions", label: "Subs", path: "/admin/subscription", icon: FiFeather },
    { id: "content", label: "Content", path: "/admin/contentmanager", icon: FiVideo },
    { id: "dietplans", label: "Diet", path: "/admin/dietplans", icon: MdOutlineSoupKitchen },
    { id: "priority", label: "Priority", path: "/admin/highpriorityuser", icon: FiAlertTriangle },
    { id: "analytics", label: "Analytics", path: "/admin/reports&analytics", icon: FiBarChart2 },
    { id: "notifications", label: "Alerts", path: "/admin/notifications", icon: FiBell },
    { id: "settings", label: "Settings", path: "/admin/settings", icon: FiSettings },
  ];

  // Secondary Modules inside the slide-up Drawer Sheet
  const sheetGroups = [
    {
      title: "Members & Access",
      items: [
        { label: "User Management", path: "/admin/usermanagement", icon: FiUsers, badge: counts.users ? `${counts.users} users` : null },
        { label: "Subscriptions", path: "/admin/subscription", icon: FiFeather, badge: counts.subs ? `${counts.subs} active` : null },
        { label: "User Queries", path: "/admin/queries", icon: FiMessageSquare, badge: counts.queries ? `${counts.queries} pending` : null, alert: true },
        { label: "High Priority Users", path: "/admin/highpriorityuser", icon: FiAlertTriangle, badge: counts.priority ? `${counts.priority} alerts` : null, alert: true },
      ]
    },
    {
      title: "Content & Broadcasts",
      items: [
        { label: "Content Manager", path: "/admin/contentmanager", icon: FiVideo },
        { label: "Live Sessions Room", path: "/admin/livesession", icon: FiCalendar, badge: counts.sessions ? `${counts.sessions} live` : null, green: true },
        { label: "Diet & Nutrition Plans", path: "/admin/dietplans", icon: MdOutlineSoupKitchen },
      ]
    },
    {
      title: "System & Reports",
      items: [
        { label: "Reports & Analytics", path: "/admin/reports&analytics", icon: FiBarChart2 },
        { label: "Notification Center", path: "/admin/notifications", icon: FiBell },
        { label: "System Settings", path: "/admin/settings", icon: FiSettings },
      ]
    }
  ];

  return (
    <>
      {/* ── MOBILE SCROLLABLE BOTTOM DOCK (Strictly md:hidden) ── */}
      <nav
        aria-label="Admin Mobile Navigation Dock"
        className="md:hidden fixed bottom-3 inset-x-3 z-[60] pointer-events-auto"
        style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
      >
        {/* Soft brand glow beneath the dock */}
        <div className="absolute inset-x-6 -bottom-1 h-10 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(107,138,90,0.38), transparent 70%)", filter: "blur(14px)" }} />

        {/* Gradient border ring */}
        <div className="relative rounded-[28px] p-[1.5px]"
          style={{
            background: "linear-gradient(120deg, rgba(107,138,90,0.85), rgba(255,255,255,0.9) 35%, rgba(255,255,255,0.9) 65%, rgba(242,154,12,0.75))",
            boxShadow: "0 18px 44px -8px rgba(30,50,20,0.28), 0 4px 14px rgba(0,0,0,0.06)",
          }}>
          <div className="relative flex items-center h-[66px] rounded-[26.5px] overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,250,242,0.94))",
              backdropFilter: "blur(28px) saturate(190%)",
              WebkitBackdropFilter: "blur(28px) saturate(190%)",
              boxShadow: "inset 0 1px 0 #fff",
            }}>

            {/* Scrollable nav items */}
            <motion.div
              ref={scrollContainerRef}
              layoutScroll
              className="flex-1 min-w-0 h-full flex items-center gap-1 pl-2 pr-3 overflow-x-auto overflow-y-hidden"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 12px, #000 calc(100% - 18px), transparent 100%)",
                maskImage: "linear-gradient(90deg, transparent 0, #000 12px, #000 calc(100% - 18px), transparent 100%)",
              }}
            >
              {dockNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;

                return (
                  <motion.button
                    key={item.id}
                    data-active={isActive ? "true" : "false"}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setSheetOpen(false);
                      navigate(item.path);
                    }}
                    className="relative flex-shrink-0 min-w-[62px] h-[54px] px-2.5 rounded-[20px] flex flex-col items-center justify-center select-none border-0 outline-none bg-transparent"
                  >
                    {/* Sliding active pill */}
                    {isActive && (
                      <motion.div
                        layoutId="adminDockActivePill"
                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                        className="absolute inset-0 rounded-[20px]"
                        style={{
                          background: "linear-gradient(135deg, #6B8A5A 0%, #4E6845 100%)",
                          boxShadow: "0 8px 20px -4px rgba(78,104,69,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
                        }}
                      >
                        {/* glossy highlight */}
                        <div className="absolute inset-x-2 top-1 h-3 rounded-full"
                          style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.3), transparent)" }} />
                      </motion.div>
                    )}

                    <motion.div
                      animate={{ y: isActive ? -1 : 0, scale: isActive ? 1.1 : 1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      className="relative z-[1] flex items-center justify-center"
                    >
                      <Icon size={19} style={{ color: isActive ? "#fff" : "#6A7466", filter: isActive ? "drop-shadow(0 2px 4px rgba(0,0,0,0.18))" : "none" }} />
                      {item.badge && !isActive && (
                        <span className="absolute -top-1.5 -right-2.5 px-1 min-w-[15px] h-[15px] bg-[#F29A0C] text-white text-[8px] font-black rounded-full flex items-center justify-center border-2 border-white">
                          {item.badge}
                        </span>
                      )}
                    </motion.div>
                    <span className="relative z-[1] text-[10px] tracking-tight mt-1 whitespace-nowrap leading-none"
                      style={{ color: isActive ? "#fff" : "#7A8576", fontWeight: isActive ? 800 : 600 }}>
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Divider */}
            <div className="w-px h-9 shrink-0" style={{ background: "linear-gradient(180deg, transparent, rgba(107,138,90,0.35), transparent)" }} />

            {/* Pinned Menu button */}
            <div className="shrink-0 px-2">
              <motion.button
                whileTap={{ scale: 0.88 }}
                onClick={() => setSheetOpen(!sheetOpen)}
                aria-label={sheetOpen ? "Close menu" : "Open menu"}
                className="relative w-[50px] h-[50px] rounded-[18px] flex items-center justify-center border-0 outline-none select-none"
                animate={{
                  background: sheetOpen
                    ? "linear-gradient(135deg, #6B8A5A 0%, #4E6845 100%)"
                    : "linear-gradient(135deg, #2A2118 0%, #1F1F1F 100%)",
                }}
                style={{ boxShadow: sheetOpen ? "0 8px 20px -4px rgba(78,104,69,0.6)" : "0 8px 20px -6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)" }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={sheetOpen ? "close" : "open"}
                    initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.18 }}
                    className="flex items-center justify-center"
                  >
                    {sheetOpen
                      ? <FiX size={21} color="#fff" />
                      : <FiMenu size={21} color="#8BA47A" />}
                  </motion.span>
                </AnimatePresence>
                {!sheetOpen && counts.queries > 0 && (
                  <>
                    <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-[#F29A0C] animate-ping opacity-60" />
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#F29A0C] text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white">
                      {counts.queries > 9 ? "9+" : counts.queries}
                    </span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── MOBILE SLIDE-UP DRAWER SHEET (Strictly md:hidden) ── */}
      <AnimatePresence>
        {sheetOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
            />

            {/* Bottom Sheet */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 360, damping: 32 }}
              className="absolute inset-x-0 bottom-0 max-h-[84vh] bg-[#FFF8EE] rounded-t-[34px] flex flex-col shadow-2xl overflow-hidden border-t border-white/80"
              style={{ paddingBottom: "max(84px, calc(env(safe-area-inset-bottom) + 82px))" }}
            >
              {/* Drag Handle & Header */}
              <div className="pt-3 pb-2.5 px-5 flex items-center justify-between border-b border-[#EFE3D2]/70">
                <div className="w-8" />
                <div className="w-12 h-1.5 rounded-full bg-stone-300 mx-auto" />
                <button
                  onClick={() => setSheetOpen(false)}
                  className="w-8 h-8 rounded-full bg-white border border-[#EFE3D2] flex items-center justify-center text-stone-600 active:scale-95"
                  aria-label="Close menu"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {/* Admin Status Card */}
                <div className="flex items-center gap-3.5 p-3.5 rounded-[22px] bg-white border border-[#EFE3D2] shadow-sm">
                  <div className="w-12 h-12 rounded-[18px] overflow-hidden bg-gradient-to-tr from-[#6B8A5A] to-[#8BA47A] text-white font-black text-lg flex items-center justify-center shadow-md shrink-0">
                    {adminProfile.imageUrl
                      ? <img src={adminProfile.imageUrl} alt={adminProfile.name} className="w-full h-full object-cover" />
                      : adminProfile.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-stone-900 truncate">
                        {adminProfile.name}
                      </h4>
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-800 shrink-0">
                        Admin Active
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 truncate mt-0.5">
                      Full Portal Administration Privileges
                    </p>
                  </div>
                </div>

                {/* Module Groups */}
                {sheetGroups.map((grp) => (
                  <div key={grp.title} className="space-y-1.5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-stone-400 px-2">
                      {grp.title}
                    </p>
                    <div className="grid grid-cols-1 gap-1">
                      {grp.items.map((item) => {
                        const Icon = item.icon;
                        const isCurrent = location.pathname === item.path;
                        return (
                          <button
                            key={item.label}
                            onClick={() => {
                              setSheetOpen(false);
                              navigate(item.path);
                            }}
                            className={`flex items-center justify-between p-3 rounded-[18px] transition-all text-left ${
                              isCurrent 
                                ? 'bg-emerald-500/15 text-[#27500A] font-bold border border-emerald-500/25' 
                                : 'bg-white hover:bg-stone-50 text-stone-700 font-medium border border-[#EFE3D2]/60'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isCurrent ? 'bg-[#6B8A5A] text-white shadow-sm' : 'bg-stone-100 text-stone-600'}`}>
                                <Icon size={18} />
                              </div>
                              <span className="text-xs tracking-tight">{item.label}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              {item.badge && (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  item.alert ? 'bg-amber-100 text-amber-800' :
                                  item.green ? 'bg-emerald-100 text-emerald-800' :
                                  'bg-stone-100 text-stone-700'
                                }`}>
                                  {item.badge}
                                </span>
                              )}
                              <FiChevronRight className="text-stone-300" size={14} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Logout Button */}
                <div className="pt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 p-3.5 rounded-[20px] bg-rose-500/10 text-rose-700 font-bold text-xs border border-rose-500/20 active:scale-[0.98] transition-all"
                  >
                    <FiLogOut size={16} />
                    <span>Log Out Admin Session</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
